class SidalihFreeGiftController {
  constructor(root = document.querySelector('[data-sidalih-free-gift-controller]')) {
    this.root = root;
    if (!this.root) return;

    this.widget = this.root.querySelector('[data-sidalih-gift-widget]');
    this.popup = this.root.querySelector('[data-sidalih-gift-popup]');
    this.launcher = this.root.querySelector('[data-sidalih-gift-launcher]');

    this.shippingEnabled = this.root.dataset.enableShipping !== 'false';
    this.giftEnabled = this.root.dataset.enableGift !== 'false';

    this.shippingGoal = Number(this.root.dataset.freeShipping || 0) * 100;
    this.giftGoal = Number(this.root.dataset.giftThreshold || 0) * 100;

    this.messages = {
      shipping: this.root.dataset.shippingMessage || 'Spend [amount] more to get Free Shipping.',
      gift: this.root.dataset.giftMessage || 'Spend [amount] more to unlock your free gift.',
      success: this.root.dataset.successMessage || "Congratulations! You've unlocked your rewards."
    };

    this.currencySymbol =
      this.root.querySelector('.sidalih-riyal-symbol-template')?.innerHTML.trim() || '';

    this.cart = null;
    this.cartTotal = 0;
    this.hasGift = false;
    this.currentGiftLine = null;
    this.isLoadingGift = false;
    this.isRefreshing = false;
    this.refreshQueued = false;
    this.refreshAgain = false;
    this.refreshTimer = null;

    this.init();
  }

  async init() {
    this.applyFeatureState();
    this.ensureWidgetLayer();
    this.bindPopupEvents();
    this.bindCartEvents();
    this.observeDynamicContent();
    this.interceptCartRequests();
    await this.refresh();
  }

  ensureWidgetLayer() {
    if (!this.widget) return;

    // Shopify's theme editor re-renders the section when a setting changes.
    // Remove any widget left in body by the previous section render first.
    document
      .querySelectorAll('body > [data-sidalih-gift-widget]')
      .forEach((widget) => {
        if (widget !== this.widget) widget.remove();
      });

    // Keep the widget above theme stacking/overflow contexts.
    if (this.widget.parentElement !== document.body) {
      document.body.appendChild(this.widget);
    }
  }

  applyFeatureState() {
    document.documentElement.classList.toggle(
      'sidalih-free-shipping-disabled',
      !this.shippingEnabled
    );
    document.documentElement.classList.toggle(
      'sidalih-free-gift-disabled',
      !this.giftEnabled
    );
    document.documentElement.classList.toggle(
      'sidalih-free-gift-system-disabled',
      !this.shippingEnabled && !this.giftEnabled
    );

    if (this.widget) this.widget.hidden = !this.giftEnabled;
  }

  isArabic() {
    return (document.documentElement.lang || '').toLowerCase().startsWith('ar');
  }

  t(ar, en) {
    return this.isArabic() ? ar : en;
  }

  isPopupOpen() {
    return !!this.popup?.classList.contains('is-open');
  }

  bindPopupEvents() {
    this.launcher?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!this.giftEnabled) return;

      if (this.isPopupOpen()) {
        this.closePopup();
      } else {
        this.openPopup();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closePopup();
    });

    document.addEventListener('click', (event) => {
      if (!this.isPopupOpen() || !this.widget) return;
      if (!this.widget.contains(event.target)) this.closePopup();
    });

    if (!this.popup) return;

    this.popup.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-gift-variant]');
      if (!button || !this.giftEnabled) return;

      event.preventDefault();
      event.stopPropagation();

      if (!this.isGiftEligible() || this.hasGift || this.isLoadingGift) return;

      button.classList.add('is-loading');

      try {
        await this.selectGift(button.dataset.giftVariant);
      } finally {
        button.classList.remove('is-loading');
        this.updateGiftExperience();
      }
    });
  }

  bindCartEvents() {
    ['cart:update', 'cart:reload', 'cart:refresh'].forEach((eventName) => {
      document.addEventListener(eventName, () => this.queueRefresh(0));
    });

    // Kalles quantity controls. The actual cart request is also intercepted,
    // but this keeps the gift UI responsive even if the theme redraw is delayed.
    document.addEventListener('click', (event) => {
      const qtyButton = event.target.closest(
        'button[name="plus"], button[name="minus"], wrapp-remove-item-oncart a, .hdt-mini-cart__remove'
      );

      if (qtyButton) {
        this.queueRefresh(120);
      }
    }, true);

    window.addEventListener('pageshow', () => this.queueRefresh(0));

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.queueRefresh(0);
    });
  }

  observeDynamicContent() {
    const observer = new MutationObserver((mutations) => {
      const hasRelevantUpdate = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) =>
          node.nodeType === 1 &&
          (
            node.matches?.(
              '[data-sidalih-free-gift-bar], hdt-quantity-wrapp, .hdt-mini-cart__actions'
            ) ||
            node.querySelector?.(
              '[data-sidalih-free-gift-bar], hdt-quantity-wrapp, .hdt-mini-cart__actions'
            )
          )
        )
      );

      if (!hasRelevantUpdate) return;

      this.queueRefresh(0);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  interceptCartRequests() {
    if (window.__sidalihCartFetchIntercepted) return;
    window.__sidalihCartFetchIntercepted = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const input = args[0];
      const url = typeof input === 'string' ? input : input?.url || '';

      if (/\/cart\/(add|change|update|clear)\.js(?:\?|$)/.test(url)) {
        window.__sidalihFreeGiftController?.queueRefresh(0);
      }

      return response;
    };
  }

  queueRefresh(delay = 0) {
    // Ignore listeners belonging to an older theme-editor render.
    if (
      window.__sidalihFreeGiftController &&
      window.__sidalihFreeGiftController !== this
    ) {
      return;
    }

    clearTimeout(this.refreshTimer);

    this.refreshQueued = true;

    this.refreshTimer = setTimeout(async () => {
      this.refreshQueued = false;

      if (this.isRefreshing) {
        this.refreshAgain = true;
        return;
      }

      await this.refresh();
    }, delay);
  }

  async getCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  }

  async refresh() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;

    try {
      const cart = await this.getCart();

      this.cart = cart;
      this.cartTotal = cart.total_price;
      this.hasGift = false;
      this.currentGiftLine = null;

      cart.items.forEach((item, index) => {
        if (item.properties?._sidalih_free_gift) {
          this.hasGift = true;
          this.currentGiftLine = index + 1;
        }
      });

      if (!this.giftEnabled && this.hasGift) {
        await this.removeGift();
        return;
      }

      if (this.giftEnabled && this.cartTotal < this.giftGoal && this.hasGift) {
        await this.removeGift();
        return;
      }

      this.updateAllBars();
      this.lockGiftQuantityControls();
      this.updateGiftExperience();
    } catch (error) {
      console.error('Sidalih Free Gift:', error);
    } finally {
      this.isRefreshing = false;

      if (this.refreshAgain) {
        this.refreshAgain = false;
        this.queueRefresh(0);
      }
    }
  }

  isGiftEligible() {
    return this.giftEnabled && this.cartTotal >= this.giftGoal;
  }

  getRemainingGiftAmount() {
    return Math.max(this.giftGoal - this.cartTotal, 0);
  }

  updateGiftExperience() {
    if (!this.giftEnabled) return;

    const eligible = this.isGiftEligible();
    const remaining = this.getRemainingGiftAmount();

    if (this.widget) {
      this.widget.classList.toggle('is-unlocked', eligible && !this.hasGift);
      this.widget.classList.toggle('has-selected-gift', this.hasGift);
    }

    if (this.launcher) {
      const dot = this.launcher.querySelector('[data-sidalih-gift-dot]');
      if (dot) {
        dot.classList.toggle('is-ready', eligible && !this.hasGift);
        dot.classList.toggle('is-selected', this.hasGift);
      }
    }

    if (!this.popup) return;

    const status = this.popup.querySelector('[data-gift-status]');
    const statusText = this.popup.querySelector('[data-gift-status-text]');

    if (status) {
      status.classList.toggle('is-locked', !eligible && !this.hasGift);
      status.classList.toggle('is-unlocked', eligible && !this.hasGift);
      status.classList.toggle('is-selected', this.hasGift);
    }

    if (statusText) {
      if (this.hasGift) {
        statusText.innerHTML = this.t(
          'تم اختيار هديتك المجانية. يمكنك إضافة هدية واحدة فقط لكل طلب.',
          'Your free gift is selected. Only one gift can be added per order.'
        );
      } else if (eligible) {
        statusText.innerHTML = this.t(
          '🎉 هديتك جاهزة! اختر منتجًا واحدًا فقط.',
          '🎉 Your gift is ready! Choose one item.'
        );
      } else {
        const amount = this.formatMoney(remaining);
        statusText.innerHTML = this.t(
          `أضف منتجات بقيمة <strong>${amount}</strong> لتفعيل الهدية.`,
          `Add <strong>${amount}</strong> more to unlock your gift.`
        );
      }
    }

    this.popup.querySelectorAll('[data-gift-variant]').forEach((button) => {
      if (button.dataset.giftSoldOut === 'true') {
        button.disabled = true;
        return;
      }

      const originalText =
        button.dataset.originalText ||
        this.t('اختر الهدية', 'Choose gift');

      if (this.hasGift) {
        button.disabled = true;
        button.classList.add('is-gift-disabled');
        button.textContent = this.t('تم اختيار هدية', 'Gift selected');
      } else if (!eligible) {
        button.disabled = true;
        button.classList.add('is-gift-disabled');
        button.textContent = this.t('أكمل طلبك', 'Locked');
      } else if (this.isLoadingGift) {
        button.disabled = true;
        button.classList.remove('is-gift-disabled');
        button.textContent = this.t('جاري الإضافة...', 'Adding...');
      } else {
        button.disabled = false;
        button.classList.remove('is-gift-disabled');
        button.textContent = originalText;
      }
    });
  }

  unlockGiftQuantityControls() {
    document
      .querySelectorAll('[data-sidalih-gift-locked="true"]')
      .forEach((wrapper) => {
        wrapper.classList.remove('sidalih-gift-quantity-locked');
        wrapper.removeAttribute('data-sidalih-gift-locked');

        const input = wrapper.querySelector('input[name="updates[]"]');

        if (input) {
          input.readOnly = false;
          input.removeAttribute('readonly');
          input.removeAttribute('aria-readonly');
          input.removeAttribute('max');
          input.removeAttribute('data-max');
          input.tabIndex = 0;
        }

        wrapper
          .querySelectorAll('button[name="minus"], button[name="plus"]')
          .forEach((button) => {
            button.disabled = false;
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.tabIndex = 0;
          });
      });
  }

  lockGiftQuantityControls() {
    this.unlockGiftQuantityControls();

    if (!this.giftEnabled || !this.hasGift || !this.currentGiftLine) return;

    const lineIndex = String(this.currentGiftLine);
    const quantityInputs = document.querySelectorAll(
      `input[name="updates[]"][data-index="${lineIndex}"]`
    );

    quantityInputs.forEach((input) => {
      const wrapper = input.closest(
        'hdt-quantity-wrapp, [data-quantity-wrapper]'
      );

      if (!wrapper) return;

      wrapper.classList.add('sidalih-gift-quantity-locked');
      wrapper.setAttribute('data-sidalih-gift-locked', 'true');

      input.value = '1';
      input.setAttribute('value', '1');
      input.setAttribute('min', '1');
      input.setAttribute('max', '1');
      input.setAttribute('data-max', '1');
      input.readOnly = true;
      input.setAttribute('aria-readonly', 'true');
      input.tabIndex = -1;

      wrapper
        .querySelectorAll('button[name="minus"], button[name="plus"]')
        .forEach((button) => {
          button.disabled = true;
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.tabIndex = -1;
        });
    });
  }

  getPageDirection() {
    const htmlDirection = document.documentElement.getAttribute('dir');
    if (htmlDirection === 'rtl' || htmlDirection === 'ltr') return htmlDirection;

    return getComputedStyle(document.documentElement).direction === 'rtl'
      ? 'rtl'
      : 'ltr';
  }

  getActiveGoal() {
    if (this.shippingEnabled && this.giftEnabled) {
      return Math.max(this.shippingGoal, this.giftGoal, 1);
    }

    if (this.shippingEnabled) return Math.max(this.shippingGoal, 1);
    if (this.giftEnabled) return Math.max(this.giftGoal, 1);

    return 1;
  }

  updateAllBars() {
    const bars = document.querySelectorAll('[data-sidalih-free-gift-bar]');
    const maxGoal = this.getActiveGoal();
    const percent = Math.min((this.cartTotal / maxGoal) * 100, 100);
    const shippingPosition = Math.min((this.shippingGoal / maxGoal) * 100, 100);
    const giftPosition = Math.min((this.giftGoal / maxGoal) * 100, 100);
    const pageDirection = this.getPageDirection();

    bars.forEach((bar) => {
      bar.hidden = !this.shippingEnabled && !this.giftEnabled;
      if (bar.hidden) return;

      const progress = bar.querySelector('.sidalih-progress__bar');
      const message = bar.querySelector('.sidalih-progress-message');
      const shippingMilestone = bar.querySelector('.sidalih-milestone.shipping');
      const giftMilestone = bar.querySelector('.sidalih-milestone.gift');
      const track = bar.querySelector('.sidalih-progress-track');

      const shippingUnlocked =
        this.shippingEnabled && this.cartTotal >= this.shippingGoal;
      const giftUnlocked =
        this.giftEnabled && this.cartTotal >= this.giftGoal;

      bar.setAttribute('dir', pageDirection);
      bar.classList.toggle('is-shipping-disabled', !this.shippingEnabled);
      bar.classList.toggle('is-gift-disabled', !this.giftEnabled);

      if (progress) progress.style.width = `${percent}%`;

      if (shippingMilestone) {
        shippingMilestone.hidden = !this.shippingEnabled;
        shippingMilestone.style.setProperty(
          '--sidalih-milestone-position',
          `${shippingPosition}%`
        );
        shippingMilestone.classList.toggle('is-success', shippingUnlocked);
      }

      if (giftMilestone) {
        giftMilestone.hidden = !this.giftEnabled;
        giftMilestone.style.setProperty(
          '--sidalih-milestone-position',
          `${giftPosition}%`
        );
        giftMilestone.classList.toggle('is-success', giftUnlocked);
      }

      if (track) track.setAttribute('aria-valuenow', String(Math.round(percent)));
      if (message) message.innerHTML = this.getProgressMessage();

      bar.classList.toggle(
        'is-shipping-unlocked',
        shippingUnlocked && !giftUnlocked
      );
      bar.classList.toggle('is-gift-unlocked', giftUnlocked);
    });
  }

  getProgressMessage() {
    if (!this.shippingEnabled && !this.giftEnabled) return '';

    if (
      this.shippingEnabled &&
      this.cartTotal < this.shippingGoal &&
      (!this.giftEnabled || this.shippingGoal <= this.giftGoal)
    ) {
      return this.messages.shipping.replace(
        '[amount]',
        this.formatMoney(this.shippingGoal - this.cartTotal)
      );
    }

    if (this.giftEnabled && this.cartTotal < this.giftGoal) {
      return this.messages.gift.replace(
        '[amount]',
        this.formatMoney(this.giftGoal - this.cartTotal)
      );
    }

    return this.messages.success;
  }

  formatMoney(cents) {
    const amount = new Intl.NumberFormat(
      document.documentElement.lang || 'en',
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }
    ).format(cents / 100);

    return `${amount} ${this.currencySymbol}`;
  }

  openPopup() {
    if (!this.popup || !this.giftEnabled || this.isPopupOpen()) return;

    this.popup.classList.add('is-open');
    this.popup.setAttribute('aria-hidden', 'false');

    this.launcher?.classList.add('active');
    this.launcher?.setAttribute('aria-expanded', 'true');
  }

  closePopup() {
    if (!this.popup || !this.isPopupOpen()) return;

    this.popup.classList.remove('is-open');
    this.popup.setAttribute('aria-hidden', 'true');

    this.launcher?.classList.remove('active');
    this.launcher?.setAttribute('aria-expanded', 'false');
  }

  async addToCart(variantId, properties = {}) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add gift.');
    }

    return response.json();
  }

  async changeItem(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to update cart.');
    }

    return response.json();
  }

  async selectGift(variantId) {
    if (!this.giftEnabled || !this.isGiftEligible() || this.hasGift || this.isLoadingGift) {
      return;
    }

    this.isLoadingGift = true;
    this.updateGiftExperience();

    try {
      await this.addToCart(variantId, { _sidalih_free_gift: 'true' });

      // Fetch the fresh cart immediately, then let the theme redraw its cart UI.
      await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:reload'));

      setTimeout(() => this.closePopup(), 300);
    } catch (error) {
      console.error('Sidalih Free Gift:', error);
    } finally {
      this.isLoadingGift = false;
      this.updateGiftExperience();
    }
  }

  async removeGift() {
    if (!this.currentGiftLine) return;

    try {
      await this.changeItem(this.currentGiftLine, 0);
      this.hasGift = false;
      this.currentGiftLine = null;
      this.closePopup();
      document.dispatchEvent(new CustomEvent('cart:reload'));
      await this.refresh();
    } catch (error) {
      console.error('Failed to remove free gift:', error);
    }
  }
}

function initSidalihFreeGift(root = document.querySelector('[data-sidalih-free-gift-controller]')) {
  if (!root) return;

  const controller = new SidalihFreeGiftController(root);
  window.__sidalihFreeGiftController = controller;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSidalihFreeGift(), { once: true });
} else {
  initSidalihFreeGift();
}

// Re-initialize safely when Shopify refreshes this section in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.matches?.('[data-sidalih-free-gift-controller]')
    ? event.target
    : event.target.querySelector?.('[data-sidalih-free-gift-controller]');

  if (root) initSidalihFreeGift(root);
});
